import { useState, useEffect } from 'react';
import { Ruler, Plus, X } from '../../icons/wp-icons';
import Modal from '../Common/Modal';

function AufmassModal({ isOpen, onClose, onSave, isLoading, editingAufmass = null }) {
  const [formData, setFormData] = useState({
    bezeichnung: '',
    breite: '',
    hoehe: '',
    tiefe: '',
    einheit: 'cm',
    material: '',
    notizen: ''
  });
  const [customFields, setCustomFields] = useState([]);
  const [newFieldName, setNewFieldName] = useState('');
  const [errors, setErrors] = useState({});

  // Bei Bearbeitung: Daten laden
  useEffect(() => {
    if (editingAufmass) {
      setFormData({
        bezeichnung: editingAufmass.bezeichnung || '',
        breite: editingAufmass.breite?.toString() || '',
        hoehe: editingAufmass.hoehe?.toString() || '',
        tiefe: editingAufmass.tiefe?.toString() || '',
        einheit: editingAufmass.einheit || 'cm',
        material: editingAufmass.material || '',
        notizen: editingAufmass.notizen || ''
      });
      // Eigene Felder laden
      const existingCustomFields = editingAufmass.customFields || [];
      setCustomFields(existingCustomFields);
    } else {
      resetForm();
    }
  }, [editingAufmass, isOpen]);

  const resetForm = () => {
    setFormData({
      bezeichnung: '',
      breite: '',
      hoehe: '',
      tiefe: '',
      einheit: 'cm',
      material: '',
      notizen: ''
    });
    setCustomFields([]);
    setNewFieldName('');
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.bezeichnung.trim()) {
      newErrors.bezeichnung = 'Bezeichnung ist erforderlich';
    }

    if (!formData.breite || parseFloat(formData.breite) <= 0) {
      newErrors.breite = 'Breite ist erforderlich';
    }

    if (!formData.hoehe || parseFloat(formData.hoehe) <= 0) {
      newErrors.hoehe = 'Höhe ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Eigenes Feld hinzufügen
  const handleAddCustomField = () => {
    if (newFieldName.trim()) {
      setCustomFields(prev => [...prev, { name: newFieldName.trim(), value: '' }]);
      setNewFieldName('');
    }
  };

  // Eigenes Feld aktualisieren
  const handleCustomFieldChange = (index, value) => {
    setCustomFields(prev => prev.map((field, i) =>
      i === index ? { ...field, value } : field
    ));
  };

  // Eigenes Feld löschen
  const handleRemoveCustomField = (index) => {
    setCustomFields(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Aufmaß-Objekt erstellen
      const aufmass = {
        id: editingAufmass?.id || crypto.randomUUID(),
        bezeichnung: formData.bezeichnung.trim(),
        breite: parseFloat(formData.breite),
        hoehe: parseFloat(formData.hoehe),
        tiefe: formData.tiefe ? parseFloat(formData.tiefe) : null,
        einheit: formData.einheit,
        material: formData.material.trim() || null,
        notizen: formData.notizen.trim() || null,
        customFields: customFields.filter(f => f.value.trim()),
        erstelltAm: editingAufmass?.erstelltAm || new Date().toISOString()
      };
      onSave(aufmass, !!editingAufmass);
      resetForm();
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isEditing = !!editingAufmass;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEditing ? 'Aufmaß bearbeiten' : 'Aufmaß erstellen'} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Info-Box */}
        <div className="flex items-start gap-3 p-3 bg-primary-light rounded-lg">
          <Ruler className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-sm text-gray-700">
            {isEditing
              ? 'Bearbeiten Sie die Maße für dieses Aufmaß.'
              : 'Erfassen Sie die Maße für diese Anfrage. Sie können mehrere Aufmaße pro Anfrage anlegen.'
            }
          </p>
        </div>

        {/* Bezeichnung */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Bezeichnung <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            name="bezeichnung"
            value={formData.bezeichnung}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.bezeichnung ? 'border-danger' : 'border-border'
            }`}
            placeholder="z.B. Fenster Küche links"
            disabled={isLoading}
          />
          {errors.bezeichnung && (
            <p className="text-danger text-sm mt-1">{errors.bezeichnung}</p>
          )}
        </div>

        {/* Maße in einer Reihe */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {/* Breite */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Breite <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              name="breite"
              value={formData.breite}
              onChange={handleChange}
              min="0"
              step="0.1"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.breite ? 'border-danger' : 'border-border'
              }`}
              placeholder="120"
              disabled={isLoading}
            />
            {errors.breite && (
              <p className="text-danger text-sm mt-1">{errors.breite}</p>
            )}
          </div>

          {/* Höhe */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Höhe <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              name="hoehe"
              value={formData.hoehe}
              onChange={handleChange}
              min="0"
              step="0.1"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.hoehe ? 'border-danger' : 'border-border'
              }`}
              placeholder="80"
              disabled={isLoading}
            />
            {errors.hoehe && (
              <p className="text-danger text-sm mt-1">{errors.hoehe}</p>
            )}
          </div>

          {/* Tiefe/Stärke */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Tiefe/Stärke
            </label>
            <input
              type="number"
              name="tiefe"
              value={formData.tiefe}
              onChange={handleChange}
              min="0"
              step="0.1"
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="6"
              disabled={isLoading}
            />
          </div>

          {/* Einheit */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Einheit
            </label>
            <select
              name="einheit"
              value={formData.einheit}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              disabled={isLoading}
            >
              <option value="cm">cm</option>
              <option value="mm">mm</option>
              <option value="m">m</option>
            </select>
          </div>
        </div>

        {/* Material/Art */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Material / Art
          </label>
          <input
            type="text"
            name="material"
            value={formData.material}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="z.B. ESG Float, Eiche massiv, Isolierglas"
            disabled={isLoading}
          />
        </div>

        {/* Eigene Felder */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Eigene Felder
          </label>

          {/* Bestehende eigene Felder */}
          {customFields.length > 0 && (
            <div className="space-y-2 mb-3">
              {customFields.map((field, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <span className="text-sm text-gray-600 min-w-[100px]">{field.name}:</span>
                  <input
                    type="text"
                    value={field.value}
                    onChange={(e) => handleCustomFieldChange(index, e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    placeholder={`Wert für ${field.name}`}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveCustomField(index)}
                    className="p-1.5 text-gray-400 hover:text-danger transition"
                    disabled={isLoading}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Neues Feld hinzufügen */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newFieldName}
              onChange={(e) => setNewFieldName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomField())}
              className="flex-1 px-3 py-1.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              placeholder="Feldname eingeben..."
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={handleAddCustomField}
              disabled={!newFieldName.trim() || isLoading}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Plus className="w-4 h-4" />
              Hinzufügen
            </button>
          </div>
        </div>

        {/* Notizen */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Notizen / Besonderheiten
          </label>
          <textarea
            name="notizen"
            value={formData.notizen}
            onChange={handleChange}
            rows="3"
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="z.B. Altbau, schräge Wand, Sonderform..."
            disabled={isLoading}
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            className="flex-1 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? 'Wird gespeichert...' : isEditing ? 'Aufmaß aktualisieren' : 'Aufmaß speichern'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition disabled:opacity-50"
            disabled={isLoading}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default AufmassModal;
